import { Link } from 'react-router-dom'

import { Badge, Card, EmptyState, formatRelativeTime, Icon, LoadingBlock, SeverityBadge, useNow } from '@/design'
import { CATEGORY_LABELS } from '@/lib/insights/categoryLabels'
import { useInsightsRead } from '@/lib/insights/queries'

import { useDrillDown } from './hooks'

const VISIBLE_ALERTS = 5

/**
 * What needs attention now, most severe first: the same open alerts as
 * Insights > Alerts, which is where they are acknowledged or dismissed. Each
 * alert opens the page that explains it.
 */
export function AlertsCard() {
  const alerts = useInsightsRead('alerts', (client) => client.activeAlerts())
  const href = useDrillDown()
  const now = useNow()
  const items = alerts.data?.data ?? []

  return (
    <Card
      className="ov-alerts-card"
      title={
        <span className="ov-title-with-icon">
          Active alerts
          {items.length > 0 ? <Badge tone="warning">{alerts.data?.meta.truncated ? `${items.length}+` : items.length}</Badge> : null}
        </span>
      }
      subtitle="Open alerts on your production websites, most severe first"
      busy={alerts.isFetching}
      footer={
        <Link className="ov-footer-link" to={href({ path: '/insights/alerts' })}>
          All alerts <Icon name="arrowRight" size={14} />
        </Link>
      }
    >
      {alerts.isPending ? <LoadingBlock rows={4} /> : null}
      {alerts.isError ? <EmptyState variant="outage" compact description="Alerts could not be loaded." /> : null}
      {alerts.data && items.length === 0 ? (
        <EmptyState variant="no-results" compact title="No active alerts" description="Nothing needs attention right now." />
      ) : null}
      {items.length > 0 ? (
        <ul className="ov-alerts" aria-label="Active alerts">
          {items.slice(0, VISIBLE_ALERTS).map((alert) => (
            <li key={alert.id} data-severity={alert.severity}>
              <Link className="ov-alert" to={href(alert.drillDown)}>
                <span className="ov-alert__meta">
                  <SeverityBadge severity={alert.severity} />
                  <span className="ov-alert__category">{CATEGORY_LABELS[alert.category]}</span>
                  {alert.state === 'acknowledged' ? <Badge tone="neutral">Acknowledged</Badge> : null}
                  <span className="ov-alert__time">{formatRelativeTime(alert.occurredAt, now)}</span>
                </span>
                <span className="ov-alert__title">{alert.title}</span>
                <span className="ov-alert__subject">{alert.subject}</span>
                <span className="ov-alert__explanation">{alert.explanation}</span>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  )
}
